import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Suspense } from 'react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ParserBuilder } from 'nuqs';
import { parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { MakeModelFilter } from '@/components/Vehilces/VehicleFilters/MakeModelFilter';
import { VehicleRangeFilter } from '@/components/Vehilces/VehicleFilters/VehicleRangeFilter';
import VehicleSelectFilter from '@/components/Vehilces/VehicleFilters/VehicleSelectFilter';
import { formatCurrency } from '@/lib/formatCurrency';
import type { FilterType } from '@/lib/vehicleFilter';
import { filterVehicles, getAvailableModels, getFilterOptions, sortVehicles, vehicleSortOrders } from '@/lib/vehicleFilter';
import type { CmsVehicle } from '@/services/cms/models/CmsVehicle';
import { VehicleSortSelect } from './VehicleSortSelect';
const parseAsRange = () => parseAsArrayOf(parseAsInteger) as unknown as ParserBuilder<[number, number]>;
export interface VehicleFilterPanelProps {
  vehicles: CmsVehicle[];
  onVehiclesFiltered?: (vehicles: CmsVehicle[]) => void;
}
function mergeFilterOptions(prev: FilterType, type: keyof FilterType, value: unknown, idx?: 0 | 1) {
  if (type === 'make') {
    return {
      ...prev,
      make: value as string,
      model: ''
    };
  }
  if ((type === 'priceRange' || type === 'yearRange') && typeof idx === 'number') {
    const newRange = prev[type] ? [...prev[type]] : [];
    newRange[idx] = value as number;
    if (newRange[0] > newRange[1]) {
      if (idx === 0) {
        newRange[1] = value as number;
      } else {
        newRange[0] = value as number;
      }
    }
    return {
      ...prev,
      [type]: newRange
    };
  }
  return {
    ...prev,
    [type]: value
  };
}
export function VehicleFilterPanel(t0) {
  const $ = _c(3);
  const {
    vehicles,
    onVehiclesFiltered
  } = t0;
  let t1;
  if ($[0] !== onVehiclesFiltered || $[1] !== vehicles) {
    t1 = <Suspense><VehicleFilterPanelComponent vehicles={vehicles} onVehiclesFiltered={onVehiclesFiltered} /></Suspense>;
    $[0] = onVehiclesFiltered;
    $[1] = vehicles;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function VehicleFilterPanelComponent(t0) {
  const $ = _c(71);
  const {
    vehicles,
    onVehiclesFiltered
  } = t0;
  const [sortedVehicles, setSortedVehicles] = React.useState(vehicles);
  let availableBerths;
  let availableLocations;
  let availableMakes;
  let maxPrice;
  let maxYear;
  let minPrice;
  let minYear;
  let priceOptions;
  let region;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let yearOptions;
  if ($[0] !== vehicles) {
    ({
      minPrice,
      maxPrice,
      availableLocations,
      availableBerths,
      minYear,
      maxYear,
      availableMakes,
      priceOptions,
      yearOptions,
      region
    } = getFilterOptions(vehicles));
    t5 = useQueryStates;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = parseAsArrayOf(parseAsString).withDefault([]);
      $[16] = t1;
    } else {
      t1 = $[16];
    }
    t2 = parseAsRange().withDefault([minPrice, maxPrice]);
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = parseAsArrayOf(parseAsInteger).withDefault([]);
      $[17] = t3;
    } else {
      t3 = $[17];
    }
    t4 = parseAsRange().withDefault([minYear, maxYear]);
    $[0] = vehicles;
    $[1] = availableBerths;
    $[2] = availableLocations;
    $[3] = availableMakes;
    $[4] = maxPrice;
    $[5] = maxYear;
    $[6] = minPrice;
    $[7] = minYear;
    $[8] = priceOptions;
    $[9] = region;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = yearOptions;
  } else {
    availableBerths = $[1];
    availableLocations = $[2];
    availableMakes = $[3];
    maxPrice = $[4];
    maxYear = $[5];
    minPrice = $[6];
    minYear = $[7];
    priceOptions = $[8];
    region = $[9];
    t1 = $[10];
    t2 = $[11];
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    yearOptions = $[15];
  }
  let t6;
  let t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = parseAsString.withDefault("");
    t7 = parseAsString.withDefault("");
    t8 = parseAsStringLiteral(vehicleSortOrders).withDefault("default");
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t1 || $[22] !== t2 || $[23] !== t3 || $[24] !== t4) {
    t9 = {
      locations: t1,
      priceRange: t2,
      berths: t3,
      yearRange: t4,
      make: t6,
      model: t7,
      sortBy: t8
    };
    $[21] = t1;
    $[22] = t2;
    $[23] = t3;
    $[24] = t4;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const [filtersAndSort, setFiltersAndSort] = t5(t9);
  let t10;
  if ($[26] !== onVehiclesFiltered || $[27] !== vehicles) {
    t10 = function updateVehicles(filters) {
      const filteredVehicles = filterVehicles(vehicles, filters);
      const sortedVehicles_0 = sortVehicles(filteredVehicles, filters.sortBy);
      setSortedVehicles(sortedVehicles_0);
      onVehiclesFiltered?.(sortedVehicles_0);
    };
    $[26] = onVehiclesFiltered;
    $[27] = vehicles;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  const updateVehicles = t10;
  let t11;
  if ($[29] !== setFiltersAndSort || $[30] !== updateVehicles) {
    t11 = function handleFilterChange(type, value, idx) {
      setFiltersAndSort(prev => {
        const result = mergeFilterOptions(prev, type, value, idx);
        updateVehicles(result);
        return result;
      });
    };
    $[29] = setFiltersAndSort;
    $[30] = updateVehicles;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const handleFilterChange = t11;
  let t12;
  if ($[32] !== maxPrice || $[33] !== maxYear || $[34] !== minPrice || $[35] !== minYear || $[36] !== setFiltersAndSort) {
    t12 = function clearFilters() {
      setFiltersAndSort({
        locations: [],
        priceRange: [minPrice, maxPrice],
        berths: [],
        yearRange: [minYear, maxYear],
        make: "",
        model: "",
        sortBy: "default"
      });
    };
    $[32] = maxPrice;
    $[33] = maxYear;
    $[34] = minPrice;
    $[35] = minYear;
    $[36] = setFiltersAndSort;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const clearFilters = t12;
  let t13;
  if ($[38] !== filtersAndSort.make || $[39] !== vehicles) {
    t13 = getAvailableModels(vehicles, filtersAndSort.make);
    $[38] = filtersAndSort.make;
    $[39] = vehicles;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const availableModels = t13;
  const filtersVisible = vehicles?.length > 0;
  let t14;
  if ($[41] !== availableBerths || $[42] !== availableLocations || $[43] !== availableMakes || $[44] !== availableModels || $[45] !== clearFilters || $[46] !== filtersAndSort.berths || $[47] !== filtersAndSort.locations || $[48] !== filtersAndSort.make || $[49] !== filtersAndSort.model || $[50] !== filtersAndSort.priceRange || $[51] !== filtersAndSort.yearRange || $[52] !== filtersVisible || $[53] !== handleFilterChange || $[54] !== priceOptions || $[55] !== region || $[56] !== yearOptions) {
    t14 = filtersVisible ? <Paper sx={{
      p: 2,
      mb: 2
    }} role="region" aria-labelledby="vehicle-filters-heading"><Box sx={{
        display: "flex",
        justifyContent: "space-between",
        mb: 2,
        alignItems: "center"
      }}><Typography component="div" variant="h6" sx={{
          display: "flex",
          alignItems: "center"
        }}><FilterAltIcon /> Filters</Typography><Box sx={{
          display: "flex",
          alignItems: "center",
          gap: 2
        }}><Button size="small" onClick={clearFilters}>Clear All</Button></Box></Box><Grid container={true} spacing={4} sx={{
        mb: 2
      }}><Grid size={{
          xs: 12,
          md: 2.4
        }}><VehicleSelectFilter id="location-filter" multiple={true} label="Location" emptyLabel="All locations" options={availableLocations} value={filtersAndSort.locations} onChange={vals => handleFilterChange("locations", vals)} /></Grid><Grid size={{
          xs: 12,
          md: 2.4
        }}><VehicleSelectFilter id="berth-filter" multiple={true} label="Berths" emptyLabel="All" options={availableBerths} value={filtersAndSort.berths} onChange={vals_0 => handleFilterChange("berths", vals_0)} /></Grid><Grid size={{
          xs: 12,
          md: 2.4
        }}><MakeModelFilter id="make-model-filter" label="Make and Model" emptyLabel="All" availableMakes={availableMakes} availableModels={availableModels} make={filtersAndSort.make} model={filtersAndSort.model} onMakeChange={val => handleFilterChange("make", val)} onModelChange={val_0 => handleFilterChange("model", val_0)} /></Grid><Grid size={{
          xs: 12,
          md: 2.4
        }}><VehicleRangeFilter id="price-range-filter" label="Price Range" minLabel="From" maxLabel="To" emptyLabel="Any" options={priceOptions} value={filtersAndSort.priceRange} onChange={(idx_0, val_1) => handleFilterChange("priceRange", val_1, idx_0)} formatOption={val_2 => formatCurrency(val_2, region, true)} /></Grid><Grid size={{
          xs: 12,
          md: 2.4
        }}><VehicleRangeFilter id="year-range-filter" label="Year" minLabel="From" maxLabel="To" emptyLabel="Any" options={yearOptions} value={filtersAndSort.yearRange} onChange={(idx_1, val_3) => handleFilterChange("yearRange", val_3, idx_1)} /></Grid></Grid></Paper> : null;
    $[41] = availableBerths;
    $[42] = availableLocations;
    $[43] = availableMakes;
    $[44] = availableModels;
    $[45] = clearFilters;
    $[46] = filtersAndSort.berths;
    $[47] = filtersAndSort.locations;
    $[48] = filtersAndSort.make;
    $[49] = filtersAndSort.model;
    $[50] = filtersAndSort.priceRange;
    $[51] = filtersAndSort.yearRange;
    $[52] = filtersVisible;
    $[53] = handleFilterChange;
    $[54] = priceOptions;
    $[55] = region;
    $[56] = yearOptions;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  let t15;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      display: "flex",
      alignItems: "center",
      gap: 2,
      justifyContent: "space-between",
      mb: 2
    };
    $[58] = t15;
  } else {
    t15 = $[58];
  }
  let t16;
  if ($[59] !== sortedVehicles.length) {
    t16 = <Typography variant="h6" component="div">Showing {sortedVehicles.length} vehicles</Typography>;
    $[59] = sortedVehicles.length;
    $[60] = t16;
  } else {
    t16 = $[60];
  }
  let t17;
  if ($[61] !== filtersAndSort.sortBy || $[62] !== filtersVisible || $[63] !== handleFilterChange) {
    t17 = filtersVisible ? <VehicleSortSelect value={filtersAndSort.sortBy} onChange={val_4 => handleFilterChange("sortBy", val_4)} /> : null;
    $[61] = filtersAndSort.sortBy;
    $[62] = filtersVisible;
    $[63] = handleFilterChange;
    $[64] = t17;
  } else {
    t17 = $[64];
  }
  let t18;
  if ($[65] !== t16 || $[66] !== t17) {
    t18 = <Box sx={t15}>{t16}{t17}</Box>;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
  } else {
    t18 = $[67];
  }
  let t19;
  if ($[68] !== t14 || $[69] !== t18) {
    t19 = <>{t14}{t18}</>;
    $[68] = t14;
    $[69] = t18;
    $[70] = t19;
  } else {
    t19 = $[70];
  }
  return t19;
}